import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CalendarClock, Plus, Pencil, Trash2 } from 'lucide-react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import toast from 'react-hot-toast'
import {
  useGetLeaveBalancesQuery,
  useCreateLeaveBalanceMutation,
  useUpdateLeaveBalanceMutation,
  useDeleteLeaveBalanceMutation,
  type LeaveBalance,
} from '@/redux/service/leaveBalance'
import { LEAVE_TYPES } from '@/constant/leave'
import { DatePicker } from '@/components/ui/date-picker'
import { dateFormatDMY, dateTimeFormatDMY } from '@/lib/utils'

interface LeaveAllocationDialogProps {
  employeeId: number
  employeeName: string
  isOpen: boolean
  isClose: () => void
}

interface AllocationFormValues {
  leave_type: string
  total_assigned: string
  start_date: string
  end_date: string
}

const LeaveAllocationDialog: React.FC<LeaveAllocationDialogProps> = ({
  employeeId,
  employeeName,
  isOpen,
  isClose,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<LeaveBalance | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<LeaveBalance | null>(null)

  const { data, isLoading } = useGetLeaveBalancesQuery(
    { employee_id: employeeId, limit: 50 },
    { skip: !isOpen }
  )
  const [createLeaveBalance, { isLoading: isCreating }] =
    useCreateLeaveBalanceMutation()
  const [updateLeaveBalance, { isLoading: isUpdating }] =
    useUpdateLeaveBalanceMutation()
  const [deleteLeaveBalance, { isLoading: isDeleting }] =
    useDeleteLeaveBalanceMutation()
  const isSaving = isCreating || isUpdating

  const formik = useFormik<AllocationFormValues>({
    enableReinitialize: true,
    initialValues: {
      leave_type: editing?.leave_type ?? LEAVE_TYPES[0] ?? '',
      total_assigned: editing ? String(editing.total_assigned) : '',
      start_date: editing?.start_date?.split('T')[0] ?? '',
      end_date: editing?.end_date?.split('T')[0] ?? '',
    },
    validationSchema: Yup.object({
      leave_type: Yup.string().required('Type is required'),
      total_assigned: Yup.number()
        .min(0, 'Must be 0 or more')
        .required('Total assigned is required'),
      start_date: Yup.string().required('Start date is required'),
      end_date: Yup.string()
        .required('End date is required')
        .test(
          'after-start',
          'End date must be after start date',
          function (value) {
            return (
              !value ||
              !this.parent.start_date ||
              value >= this.parent.start_date
            )
          }
        ),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          leave_type: values.leave_type,
          total_assigned: Number(values.total_assigned),
          start_date: values.start_date,
          end_date: values.end_date,
        }
        if (editing) {
          await updateLeaveBalance({ id: editing.id, ...payload }).unwrap()
          toast.success('Leave allocation updated')
        } else {
          await createLeaveBalance({
            employee_id: employeeId,
            ...payload,
          }).unwrap()
          toast.success('Leave allocation added')
        }
        resetForm()
        setIsFormOpen(false)
        setEditing(null)
      } catch (error) {
        const message =
          (error as { data?: { message?: string } })?.data?.message ||
          'Something went wrong'
        toast.error(message)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleEdit = (allocation: LeaveBalance) => {
    setEditing(allocation)
    setIsFormOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteLeaveBalance({ id: deleteTarget.id }).unwrap()
      toast.success('Leave allocation removed')
      setDeleteTarget(null)
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to delete allocation'
      toast.error(message)
    }
  }

  const allocations = data?.data ?? []

  return (
    <>
      {/* MAIN DIALOG */}
      <Dialog open={isOpen} onOpenChange={open => !open && isClose()}>
        <DialogContent className='max-w-4xl w-full p-0 overflow-hidden bg-background border-border rounded-xl shadow-2xl'>
          <div className='flex flex-col h-full'>
            {/* HEADER */}
            <div className='px-8 py-6 border-b border-border flex items-center justify-between bg-muted/30'>
              <div className='flex items-center gap-3'>
                <div className='p-2.5 bg-primary/10 rounded-xl text-primary'>
                  <CalendarClock className='w-5 h-5' />
                </div>
                <div className='space-y-1'>
                  <DialogTitle className='text-2xl font-bold text-foreground'>
                    Leave Allocation
                  </DialogTitle>
                  <p className='text-sm text-muted-foreground'>
                    {employeeName}
                  </p>
                </div>
              </div>

              <Button
                onClick={() => {
                  setEditing(null)
                  setIsFormOpen(true)
                }}
                className='rounded-lg px-4 py-2 flex items-center gap-2 shadow-sm'
              >
                <Plus className='h-4 w-4' />
                Add New Record
              </Button>
            </div>

            {/* CONTENT */}
            <div className='px-8 py-6 overflow-y-auto max-h-[60vh]'>
              <div className='rounded-lg border border-border overflow-hidden'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead className='text-right'>Assigned</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead className='text-right'>Consumed</TableHead>
                      <TableHead className='text-right'>Balance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className='text-right'>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className='px-4 py-12 text-center text-muted-foreground bg-background'
                        >
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : allocations.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className='px-4 py-12 text-center text-muted-foreground bg-background'
                        >
                          <div className='flex flex-col items-center gap-2'>
                            <CalendarClock className='h-8 w-8 opacity-20' />
                            <p>No leave allocations yet</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      allocations.map(a => (
                        <TableRow
                          key={a.id}
                          className='hover:bg-muted/30 transition-colors'
                        >
                          <TableCell className='font-medium text-foreground'>
                            {a.leave_type}
                          </TableCell>
                          <TableCell className='text-right'>
                            {a.total_assigned}
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {dateFormatDMY(a.start_date)}
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {dateFormatDMY(a.end_date)}
                          </TableCell>
                          <TableCell className='text-right'>
                            {a.consumed}
                          </TableCell>
                          <TableCell className='text-right font-semibold'>
                            {a.balance}
                          </TableCell>
                          <TableCell>
                            <span
                              className={
                                a.status === 'active'
                                  ? 'text-xs font-bold text-emerald-600'
                                  : 'text-xs font-bold text-muted-foreground'
                              }
                            >
                              {a.status.toUpperCase()}
                            </span>
                          </TableCell>
                          <TableCell className='text-muted-foreground text-xs whitespace-nowrap'>
                            {dateTimeFormatDMY(a.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className='flex items-center justify-end gap-1'>
                              <Button
                                variant='ghost'
                                size='icon'
                                className='h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-md transition-all'
                                onClick={() => handleEdit(a)}
                              >
                                <Pencil className='h-4 w-4' />
                              </Button>
                              <Button
                                variant='ghost'
                                size='icon'
                                className='h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-md transition-all'
                                onClick={() => setDeleteTarget(a)}
                              >
                                <Trash2 className='h-4 w-4' />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* FOOTER */}
            <div className='px-8 py-4 border-t border-border flex justify-end bg-muted/10'>
              <Button
                variant='outline'
                onClick={isClose}
                className='rounded-lg px-6 hover:bg-muted transition-all'
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ADD / EDIT DIALOG */}
      <Dialog
        open={isFormOpen}
        onOpenChange={open => {
          if (!open) {
            formik.resetForm()
            setIsFormOpen(false)
            setEditing(null)
          }
        }}
      >
        <DialogContent className='max-w-md p-6 bg-background border border-border rounded-xl shadow-2xl'>
          <DialogHeader className='space-y-1'>
            <DialogTitle className='text-xl font-bold text-foreground'>
              {editing ? 'Update Leave Allocation' : 'Add Leave Allocation'}
            </DialogTitle>
            <DialogDescription className='text-sm text-muted-foreground'>
              {editing
                ? 'Modify this leave allocation.'
                : `Allocate a new leave balance for ${employeeName}.`}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={formik.handleSubmit} className='mt-6 space-y-5'>
            <div className='space-y-2'>
              <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                Type
              </Label>
              <Select
                value={formik.values.leave_type}
                onValueChange={v => formik.setFieldValue('leave_type', v)}
              >
                <SelectTrigger className='h-10 rounded-lg'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                Total Assigned
              </Label>
              <Input
                type='number'
                min={0}
                step='0.5'
                name='total_assigned'
                value={formik.values.total_assigned}
                onChange={formik.handleChange}
                className='h-10 rounded-lg'
              />
              {formik.touched.total_assigned &&
                formik.errors.total_assigned && (
                  <p className='text-[11px] font-semibold text-red-500'>
                    {formik.errors.total_assigned}
                  </p>
                )}
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                  Start Date
                </Label>
                <DatePicker
                  value={formik.values.start_date}
                  onChange={v => formik.setFieldValue('start_date', v)}
                  placeholder='Pick date'
                  triggerClassName='h-10 rounded-lg'
                />
                {formik.touched.start_date && formik.errors.start_date && (
                  <p className='text-[11px] font-semibold text-red-500'>
                    {formik.errors.start_date}
                  </p>
                )}
              </div>

              <div className='space-y-2'>
                <Label className='text-xs font-bold uppercase tracking-wider text-muted-foreground'>
                  End Date
                </Label>
                <DatePicker
                  value={formik.values.end_date}
                  onChange={v => formik.setFieldValue('end_date', v)}
                  placeholder='Pick date'
                  triggerClassName='h-10 rounded-lg'
                  minDate={formik.values.start_date || undefined}
                />
                {formik.touched.end_date && formik.errors.end_date && (
                  <p className='text-[11px] font-semibold text-red-500'>
                    {formik.errors.end_date}
                  </p>
                )}
              </div>
            </div>

            <div className='flex justify-end gap-3 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  formik.resetForm()
                  setIsFormOpen(false)
                  setEditing(null)
                }}
                className='px-6 rounded-lg border-border text-muted-foreground hover:bg-accent'
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={isSaving}
                className='px-8 rounded-lg shadow-sm'
              >
                {isSaving
                  ? 'Saving...'
                  : editing
                    ? 'Save Changes'
                    : 'Add Record'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={open => !open && setDeleteTarget(null)}
      >
        <DialogContent className='max-w-sm p-6 bg-background border border-border rounded-xl shadow-2xl'>
          <DialogHeader className='space-y-2'>
            <div className='h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center mb-2'>
              <Trash2 className='h-6 w-6 text-destructive' />
            </div>
            <DialogTitle className='text-xl font-bold'>
              Confirm Delete
            </DialogTitle>
            <DialogDescription className='text-sm text-muted-foreground'>
              Are you sure you want to delete this{' '}
              {deleteTarget?.leave_type ?? ''} allocation? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>

          <div className='flex justify-end gap-3 mt-6'>
            <Button
              variant='outline'
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
              className='flex-1 rounded-lg border-border'
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className='flex-1 rounded-lg'
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default LeaveAllocationDialog
